<template>
  <Listbox
    v-model="selectedValue"
    as="div"
    class="relative"
    :multiple="multiple"
    :disabled="disabled"
  >
    <ListboxButton
      :class="[
        'flex h-9 w-full items-center gap-2 rounded-theme-sm border border-theme-border bg-theme-surface px-3 text-left text-sm shadow-sm transition-colors dark:border-theme-border dark:bg-theme-surface-raised',
        disabled
          ? 'cursor-not-allowed opacity-60'
          : 'hover:border-theme-border-strong focus:outline-none focus:ring-2 focus:ring-theme-palette-emerald/20 dark:hover:border-theme-border',
        buttonClass,
      ]"
      :data-test="dataTest"
    >
      <!-- An optional trigger, for callers that need the control to read as an
           icon button rather than as a labelled select. Default is unchanged. -->
      <slot name="trigger" :selected-label="selectedLabel" :has-selection="hasSelection">
        <span
          class="min-w-0 flex-1 truncate"
          :class="
            hasSelection ? 'text-theme-text' : 'text-theme-text-muted'
          "
        >
          {{ selectedLabel }}
        </span>
        <icon :path="mdiChevronDown" size="16" class="shrink-0 text-theme-text-muted" />
      </slot>
    </ListboxButton>

    <transition
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <ListboxOptions
        :class="[
          'absolute z-dropdown mt-1 w-full min-w-56 rounded-theme-sm border border-theme-border bg-theme-surface p-1 shadow-theme-dialog dark:shadow-theme-shadow/60 focus:outline-none dark:border-theme-border dark:bg-theme-surface-raised',
          optionsClass,
        ]"
      >
        <ListboxOption
          v-for="option in options"
          :key="String(option.value)"
          v-slot="{ active, selected }"
          :value="option.value"
          as="template"
          :disabled="option.disabled"
        >
          <li
            :class="[
              'flex cursor-pointer items-start gap-2 rounded-md px-3 py-2 text-sm',
              active ? 'bg-theme-palette-emerald/10 dark:bg-theme-surface-elevated' : '',
              option.disabled ? 'cursor-not-allowed opacity-50' : '',
              selected
                ? 'font-semibold text-theme-palette-emerald dark:text-theme-palette-emerald'
                : 'text-theme-text',
              optionClass,
            ]"
          >
            <span class="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center">
              <icon v-if="selected" :path="mdiCheck" size="14" />
            </span>
            <slot name="option" :option="option" :selected="selected" :active="active">
            <span class="min-w-0">
              <span class="block truncate">{{ option.label }}</span>
              <span
                v-if="option.description"
                class="mt-0.5 block text-xs font-normal text-theme-text-muted"
              >
                {{ option.description }}
              </span>
            </span>
            </slot>
          </li>
        </ListboxOption>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon';
  import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue';
  import { mdiCheck, mdiChevronDown } from '@mdi/js';
  import { computed } from 'vue';

  const props = defineProps({
    modelValue: { type: [String, Number, Boolean, Array, Object], default: null },
    options: { type: Array, default: () => [] },
    multiple: { type: Boolean, default: false },
    placeholder: { type: String, default: 'Select' },
    buttonClass: { type: [String, Array, Object], default: '' },
    optionsClass: { type: [String, Array, Object], default: '' },
    optionClass: { type: [String, Array, Object], default: '' },
    disabled: { type: Boolean, default: false },
    dataTest: { type: String, default: 'app-dropdown' },
    // Multi-select only: list every selected label (joined) while the selection
    // count is ≤ collapseAfter; beyond that, collapse to "first (+N)". Default 1
    // preserves the historic "first (+N)" behaviour for any selection > 1.
    collapseAfter: { type: Number, default: 1 },
  });

  const emit = defineEmits(['update:modelValue']);

  const selectedValue = computed({
    get() {
      return props.multiple
        ? Array.isArray(props.modelValue)
          ? props.modelValue
          : []
        : props.modelValue;
    },
    set(value) {
      emit('update:modelValue', value);
    },
  });

  const selectedOptions = computed(() => {
    if (props.multiple) {
      const selected = new Set(selectedValue.value);
      return props.options.filter((option) => selected.has(option.value));
    }
    return props.options.filter((option) => option.value === selectedValue.value);
  });

  const hasSelection = computed(() => selectedOptions.value.length > 0);

  const selectedLabel = computed(() => {
    if (!hasSelection.value) return props.placeholder;
    if (!props.multiple) return selectedOptions.value[0]?.label ?? props.placeholder;
    const labels = selectedOptions.value.map((option) => option.label);
    if (labels.length <= Math.max(1, props.collapseAfter)) return labels.join(', ');
    return `${labels[0]} (+${labels.length - 1})`;
  });
</script>
